import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  Users,
  BarChart3,
  Smartphone,
  Globe,
  Calendar,
  MessageCircle,
  ArrowRight,
  Star,
  Shield,
  Zap,
} from "lucide-react";

const ContactContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.gradients.background};
  padding-top: 6rem;
  overflow-x: hidden;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroSection = styled.section`
  padding: 4rem 2rem 6rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  text-align: center;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 3rem 1.5rem 5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2.5rem 1rem 4rem;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1.5rem;
  line-height: 1.2;
`;

const HeroSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const ContactGrid = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6rem;
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;
  padding: 6rem 2rem;
  align-items: flex-start;
  justify-content: center;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    gap: 4rem;
    align-items: center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
    gap: 3rem;
  }
`;

const FormSection = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 3rem;
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  width: 100%;
  max-width: 600px;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    max-width: 700px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2rem;
  }
`;

const FormTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.sizes["2xl"]};
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const FormSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const AssessmentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const FormInput = styled.input`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.sizes.base};
  background: ${({ theme }) => theme.colors.surface.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent.primary};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.sizes.base};
  background: ${({ theme }) => theme.colors.surface.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent.primary};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FormSelect = styled.select`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.sizes.base};
  background: ${({ theme }) => theme.colors.surface.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent.primary};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:hover {
    background: ${({ theme }) => theme.colors.surface.secondary};
  }
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 1.25rem;
  height: 1.25rem;
  accent-color: ${({ theme }) => theme.colors.accent.primary};
`;

const SubmitButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.25rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  box-shadow: ${({ theme }) => theme.shadows.lg};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.xl};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContactInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  width: 100%;
  max-width: 500px;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    max-width: 700px;
  }
`;

const InfoCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.elevated};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }
`;

const InfoIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.gradients.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.inverse};
  margin-bottom: 1.5rem;
`;

const InfoTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const InfoDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const InfoDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const InfoDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
`;

const BenefitsSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.subtle};
  width: 100%;
  display: flex;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const BenefitsContainer = styled.div`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
  text-align: center;
  width: 100%;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const BenefitsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;
`;

const BenefitCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 2rem;
  text-align: center;
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  width: 100%;
  max-width: 300px;
  min-width: 250px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.elevated};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 280px;
    max-width: 100%;
  }
`;

const BenefitIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.inverse};
`;

const BenefitTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const BenefitDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    title: "",
    district: "",
    phone: "",
    studentCount: "",
    currentSystems: "",
    challenges: [] as string[],
    timeline: "",
    budget: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (challenge: string) => {
    setFormData((prev) => ({
      ...prev,
      challenges: prev.challenges.includes(challenge)
        ? prev.challenges.filter((c) => c !== challenge)
        : [...prev.challenges, challenge],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 2000));

    console.log("Form submitted:", formData);
    setIsSubmitting(false);

    // Reset form or show success message
    alert(
      "Thank you! We'll be in touch within 24 hours to schedule your free consultation."
    );
  };

  const challengeOptions = [
    "Low student engagement",
    "Data silos and fragmentation",
    "Outdated technology infrastructure",
    "FERPA compliance concerns",
    "Limited analytics and reporting",
    "Mobile accessibility issues",
    "Teacher adoption challenges",
    "Parent communication gaps",
  ];

  const benefits = [
    {
      icon: <CheckCircle size={40} />,
      title: "Free 30-Minute Consultation",
      description:
        "Get personalized insights into your district's technology needs and opportunities.",
    },
    {
      icon: <BarChart3 size={40} />,
      title: "Custom Technology Assessment",
      description:
        "Receive a detailed analysis of your current systems and recommended improvements.",
    },
    {
      icon: <Star size={40} />,
      title: "Proven Track Record",
      description:
        "Join 50+ districts that have successfully transformed their educational technology.",
    },
    {
      icon: <Shield size={40} />,
      title: "FERPA-Compliant Solutions",
      description:
        "All our platforms meet the highest standards for student data privacy and security.",
    },
  ];

  return (
    <ContactContainer>
      <HeroSection>
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Let's Transform Your District Together
          </HeroTitle>

          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Schedule your free consultation and discover how we can help engage
            your students while providing educators with powerful, actionable
            insights.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ContactGrid>
        <FormSection
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <FormTitle>Free District Assessment</FormTitle>
          <FormSubtitle>
            Tell us about your district's needs and challenges. We'll provide a
            customized technology roadmap and schedule a consultation with our
            education experts.
          </FormSubtitle>

          <AssessmentForm onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel htmlFor="name">Full Name *</FormLabel>
              <FormInput
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="email">Email Address *</FormLabel>
              <FormInput
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="title">Title/Position *</FormLabel>
              <FormInput
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Superintendent, CTO, Technology Director"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="district">
                School District/Organization *
              </FormLabel>
              <FormInput
                id="district"
                name="district"
                type="text"
                value={formData.district}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="phone">Phone Number</FormLabel>
              <FormInput
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="studentCount">
                Approximate Student Count *
              </FormLabel>
              <FormSelect
                id="studentCount"
                name="studentCount"
                value={formData.studentCount}
                onChange={handleInputChange}
                aria-label="Select approximate student count"
                required
              >
                <option value="">Select student count</option>
                <option value="under-1000">Under 1,000</option>
                <option value="1000-5000">1,000 - 5,000</option>
                <option value="5000-15000">5,000 - 15,000</option>
                <option value="15000-50000">15,000 - 50,000</option>
                <option value="over-50000">Over 50,000</option>
              </FormSelect>
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="currentSystems">
                Current Technology Systems
              </FormLabel>
              <FormTextarea
                id="currentSystems"
                name="currentSystems"
                value={formData.currentSystems}
                onChange={handleInputChange}
                placeholder="Describe your current student information system, LMS, and other educational technology tools..."
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Primary Challenges (Select all that apply)</FormLabel>
              <CheckboxGroup>
                {challengeOptions.map((challenge) => (
                  <CheckboxItem key={challenge}>
                    <Checkbox
                      checked={formData.challenges.includes(challenge)}
                      onChange={() => handleCheckboxChange(challenge)}
                    />
                    {challenge}
                  </CheckboxItem>
                ))}
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="timeline">Implementation Timeline</FormLabel>
              <FormSelect
                id="timeline"
                name="timeline"
                value={formData.timeline}
                onChange={handleInputChange}
                aria-label="Select implementation timeline"
              >
                <option value="">Select timeline</option>
                <option value="immediate">ASAP (Within 3 months)</option>
                <option value="next-semester">
                  Next semester (3-6 months)
                </option>
                <option value="next-year">
                  Next school year (6-12 months)
                </option>
                <option value="future">Future planning (12+ months)</option>
              </FormSelect>
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="budget">Estimated Budget Range</FormLabel>
              <FormSelect
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
                aria-label="Select estimated budget range"
              >
                <option value="">Select budget range</option>
                <option value="under-50k">Under $50,000</option>
                <option value="50k-100k">$50,000 - $100,000</option>
                <option value="100k-250k">$100,000 - $250,000</option>
                <option value="250k-500k">$250,000 - $500,000</option>
                <option value="over-500k">$500,000+</option>
              </FormSelect>
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="message">Additional Information</FormLabel>
              <FormTextarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us more about your specific needs, goals, or any questions you have..."
              />
            </FormGroup>

            <SubmitButton
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
            >
              {isSubmitting ? (
                <>Processing...</>
              ) : (
                <>
                  <Send size={20} />
                  Schedule Free Consultation
                </>
              )}
            </SubmitButton>
          </AssessmentForm>
        </FormSection>

        <ContactInfo
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <InfoCard whileHover={{ y: -4 }} transition={{ duration: 0.3 }}>
            <InfoIcon>
              <MessageCircle size={30} />
            </InfoIcon>
            <InfoTitle>Get in Touch</InfoTitle>
            <InfoDescription>
              Ready to start your transformation? Our education technology
              experts are here to help.
            </InfoDescription>
            <InfoDetails>
              <InfoDetail>
                <Mail size={18} />
                contact@swintek.com
              </InfoDetail>
              <InfoDetail>
                <Phone size={18} />
                1-800-SWINTEK (1-800-794-6835)
              </InfoDetail>
              <InfoDetail>
                <MapPin size={18} />
                123 Education Way, Tech Valley, CA 94000
              </InfoDetail>
            </InfoDetails>
          </InfoCard>

          <InfoCard whileHover={{ y: -4 }} transition={{ duration: 0.3 }}>
            <InfoIcon>
              <Clock size={30} />
            </InfoIcon>
            <InfoTitle>Office Hours</InfoTitle>
            <InfoDescription>
              We're available when you need us most - during school hours and
              beyond.
            </InfoDescription>
            <InfoDetails>
              <InfoDetail>
                <span>Monday - Friday:</span>
                <span>6:00 AM - 6:00 PM PST</span>
              </InfoDetail>
              <InfoDetail>
                <span>Weekend Support:</span>
                <span>Emergency situations only</span>
              </InfoDetail>
              <InfoDetail>
                <span>Response Time:</span>
                <span>Within 24 hours</span>
              </InfoDetail>
            </InfoDetails>
          </InfoCard>

          <InfoCard whileHover={{ y: -4 }} transition={{ duration: 0.3 }}>
            <InfoIcon>
              <Calendar size={30} />
            </InfoIcon>
            <InfoTitle>What to Expect</InfoTitle>
            <InfoDescription>
              Here's what happens after you submit your assessment.
            </InfoDescription>
            <InfoDetails>
              <InfoDetail>
                <CheckCircle size={18} />
                Initial review within 24 hours
              </InfoDetail>
              <InfoDetail>
                <CheckCircle size={18} />
                Consultation scheduled within 48 hours
              </InfoDetail>
              <InfoDetail>
                <CheckCircle size={18} />
                Custom proposal within 1 week
              </InfoDetail>
            </InfoDetails>
          </InfoCard>
        </ContactInfo>
      </ContactGrid>

      <BenefitsSection>
        <BenefitsContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Choose Swintek?
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            We're committed to your success from consultation to implementation
            and beyond.
          </SectionSubtitle>

          <BenefitsGrid>
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <BenefitIcon>{benefit.icon}</BenefitIcon>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </BenefitCard>
            ))}
          </BenefitsGrid>
        </BenefitsContainer>
      </BenefitsSection>
    </ContactContainer>
  );
};

export default Contact;
